import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { DatePicker } from './ui/DatePicker';
import { ArrowLeft, Save, Plus } from 'lucide-react';
import { InterestSlabRow, InterestSlab } from './InterestSlabRow';
interface InterestProfileFormProps {
  onBack: () => void;
  onSave: () => void;
}
export function InterestProfileForm({
  onBack,
  onSave
}: InterestProfileFormProps) {
  // Generate a random UUID for display
  const [profileId] = useState(`INT-${Math.random().toString(36).substr(2, 9).toUpperCase()}`);
  // Header State
  const [headerData, setHeaderData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'Flat',
    rate: '',
    glAccount: '',
    effectiveDate: ''
  });
  // Slabs State
  const [slabs, setSlabs] = useState<InterestSlab[]>([{
    id: Math.random().toString(36).substr(2, 9),
    type: 'Percentage',
    slabLow: '0',
    slabHigh: '',
    value: '',
    minCap: '',
    maxCap: '',
    glAccount: '',
    taxRules: []
  }]);
  const handleHeaderChange = (field: string, value: string) => {
    setHeaderData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleAddSlab = () => {
    const lastSlab = slabs[slabs.length - 1];
    const newLow = lastSlab && lastSlab.slabHigh ? (parseInt(lastSlab.slabHigh) + 1).toString() : '0';
    setSlabs([...slabs, {
      id: Math.random().toString(36).substr(2, 9),
      type: 'Percentage',
      slabLow: newLow,
      slabHigh: '',
      value: '',
      minCap: '',
      maxCap: '',
      glAccount: '',
      taxRules: []
    }]);
  };
  const handleRemoveSlab = (id: string) => {
    if (slabs.length > 1) {
      setSlabs(slabs.filter(s => s.id !== id));
    }
  };
  const handleSlabChange = (id: string, updates: Partial<InterestSlab>) => {
    setSlabs(slabs.map(s => s.id === id ? {
      ...s,
      ...updates
    } : s));
  };
  return <div className="flex flex-col h-full bg-gray-50" data-id="element-271">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-272">
        <div className="flex items-center" data-id="element-273">
          <Button variant="ghost" onClick={onBack} className="mr-4" data-id="element-274">
            <ArrowLeft className="h-5 w-5 mr-2" data-id="element-275" />
            Back to List
          </Button>
          <div data-id="element-276">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-277">
              New Interest/Markup Profile
            </h1>
            <p className="text-sm text-gray-500" data-id="element-278">
              Configure interest rates and slab definitions
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-279">
          <Button variant="secondary" onClick={onBack} data-id="element-280">
            Cancel
          </Button>
          <Button onClick={onSave} data-id="element-281">
            <Save className="h-4 w-4 mr-2" data-id="element-282" />
            Save Profile
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full space-y-8" data-id="element-283">
        {/* Section 1: Profile Header */}
        <section className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-284">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100" data-id="element-285">
            Profile Configuration
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-286">
            <div className="space-y-1.5" data-id="element-287">
              <label className="text-sm font-medium text-gray-700" data-id="element-288">
                Interest/Markup ID
              </label>
              <div className="flex h-10 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-id="element-289">
                {profileId}
              </div>
            </div>

            <Input label="Interest/Markup Code" value={headerData.code} onChange={e => handleHeaderChange('code', e.target.value)} placeholder="e.g., INT-STD-001" helperText="Used for integrations and product setup" required data-id="element-290" />

            <Input label="Interest/Markup Name" value={headerData.name} onChange={e => handleHeaderChange('name', e.target.value)} placeholder="e.g., Standard Personal Loan Interest" required data-id="element-291" />

            <Select label="Interest Type" value={headerData.type} onChange={e => handleHeaderChange('type', e.target.value)} options={[{
            value: 'Flat',
            label: 'Flat Rate'
          }, {
            value: 'Reducing Balance',
            label: 'Reducing Balance'
          }, {
            value: 'Compound',
            label: 'Compound Interest'
          }]} required data-id="element-292" />

            <Input label="Interest Rate (%)" type="number" value={headerData.rate} onChange={e => handleHeaderChange('rate', e.target.value)} placeholder="e.g., 15.5" helperText="Applied annually, monthly, or based on specified frequency" required data-id="element-293" />

            <Select label="GL Account" value={headerData.glAccount} onChange={e => handleHeaderChange('glAccount', e.target.value)} options={[{
            value: '4100',
            label: '4100 - Interest Income'
          }, {
            value: '4200',
            label: '4200 - Markup Revenue'
          }]} required data-id="element-294" />

            <DatePicker label="Effective Date" value={headerData.effectiveDate} onChange={e => handleHeaderChange('effectiveDate', e.target.value)} required data-id="element-295" />

            <div className="md:col-span-2" data-id="element-296">
              <label className="text-sm font-medium text-gray-700 mb-1.5 block" data-id="element-297">
                Description
              </label>
              <textarea className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50" placeholder="Describe the purpose, applicable products, and pricing policy..." value={headerData.description} onChange={e => handleHeaderChange('description', e.target.value)} data-id="element-298" />
            </div>
          </div>
        </section>

        {/* Section 2: Slab Configuration */}
        <section className="space-y-4 animate-in fade-in slide-in-from-bottom-6 duration-500 delay-100" data-id="element-299">
          <div className="flex items-center justify-between" data-id="element-300">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-301">
              Processing Fees on Interest
            </h2>
            <Button onClick={handleAddSlab} size="sm" variant="secondary" data-id="element-302">
              <Plus className="h-4 w-4 mr-2" data-id="element-303" />
              Add Slab
            </Button>
          </div>

          <div className="space-y-4" data-id="element-304">
            {slabs.map((slab, index) => <InterestSlabRow key={slab.id} slab={slab} index={index} onChange={handleSlabChange} onRemove={handleRemoveSlab} isFirst={index === 0} data-id="element-305" />)}
          </div>
        </section>
      </div>
    </div>;
}